import type { ApiSessionData } from '../api/types';
import { SESSION_USER_KEY, IS_SCREEN_LOCKED_CACHE_KEY } from '../config';
import { StringSession } from '../lib/gramjs/sessions';

const DC_IDS = [1, 2, 3, 4, 5];
const SESSION_STRING_KEY = 'telegram_session_string';
const ACCOUNTS_KEY = 'telegram_accounts';
const CURRENT_ACCOUNT_ID_KEY = 'current_account_id';

interface AccountData {
  userId: string;
  sessionString: string;
  displayName?: string;
  firstName?: string;
  lastName?: string;
  username?: string;
  timestamp: number;
}

export function hasStoredSession() {
  if (checkSessionLocked()) {
    return true;
  }
  
  const currentSessionString = localStorage.getItem(SESSION_STRING_KEY);
  if (currentSessionString) {
    return true;
  }
  
  const userAuthJson = localStorage.getItem(SESSION_USER_KEY);
  if (!userAuthJson) {
    return false;
  }
  try {
    const userAuth = JSON.parse(userAuthJson);
    return Boolean(userAuth && userAuth.id && userAuth.dcID);
  } catch (err) {
    return false;
  }
}

export function storeSession(sessionData: ApiSessionData, currentUserId?: string) {
  const {
    mainDcId, keys, isTest,
  } = sessionData;

  try {
    const stringSession = new StringSession();
    
    stringSession.setDC(
      mainDcId,
      '149.154.175.50',
      443,
      isTest
    );
    
    Object.keys(keys).forEach((dcIdStr) => {
      const dcId = Number(dcIdStr);
      const keyData = keys[dcId];
      
      if (typeof keyData === 'string') {
        const keyBuffer = Buffer.from(keyData, 'hex');
        const authKey = new (require('../lib/gramjs/crypto/AuthKey').AuthKey)();
        authKey._key = keyBuffer;
        stringSession.setAuthKey(authKey, dcId);
      }
    });
    
    const sessionString = stringSession.save();
    
    if (!sessionString) {
      return;
    }
    
    const isSessionStringProtected = localStorage.getItem('session_string_protected') === 'true';
    if (isSessionStringProtected) {
      return;
    }
    
    localStorage.setItem(SESSION_STRING_KEY, sessionString);
    localStorage.setItem(SESSION_USER_KEY, JSON.stringify({
      dcID: mainDcId,
      id: currentUserId,
      test: isTest,
    }));
    localStorage.setItem('dc', String(mainDcId));
    
    if (currentUserId) {
      const accountData: AccountData = {
        userId: currentUserId,
        sessionString,
        displayName: `Account ${currentUserId.slice(-4)}`,
        timestamp: Date.now(),
      };
      
      const existingAccounts = getAllAccounts();
      
      const accountIndex = existingAccounts.findIndex(acc => acc.userId === currentUserId);
      if (accountIndex >= 0) {
        existingAccounts[accountIndex] = accountData;
      } else {
        existingAccounts.push(accountData);
      }
      
      localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(existingAccounts));
      localStorage.setItem(CURRENT_ACCOUNT_ID_KEY, currentUserId);
    }
  } catch (error) {
  }
}

export function clearStoredSession() {
  [
    SESSION_USER_KEY,
    'dc',
    CURRENT_ACCOUNT_ID_KEY,
    SESSION_STRING_KEY,
    ...DC_IDS.map((dcId) => `dc${dcId}_auth_key`),
    ...DC_IDS.map((dcId) => `dc${dcId}_hash`),
    ...DC_IDS.map((dcId) => `dc${dcId}_server_salt`),
  ].forEach((key) => {
    localStorage.removeItem(key);
  });
}

export function clearCurrentSessionOnly() {
  [
    SESSION_USER_KEY,
    'dc',
    CURRENT_ACCOUNT_ID_KEY,
    SESSION_STRING_KEY,
    ...DC_IDS.map((dcId) => `dc${dcId}_auth_key`),
    ...DC_IDS.map((dcId) => `dc${dcId}_hash`),
    ...DC_IDS.map((dcId) => `dc${dcId}_server_salt`),
  ].forEach((key) => {
    localStorage.removeItem(key);
  });
}

export function loadStoredSession(): ApiSessionData | undefined {
  if (!hasStoredSession()) {
    return undefined;
  }
  
  const sessionString = localStorage.getItem(SESSION_STRING_KEY);
  if (sessionString) {
    try {
      const stringSession = new StringSession(sessionString);
      stringSession.load();
      const sessionData = stringSession.getSessionData();
      
      return sessionData;
    } catch (error) {
    }
  }
  
  const userAuth = JSON.parse(localStorage.getItem(SESSION_USER_KEY)!);
  if (!userAuth) {
    return undefined;
  }
  
  const mainDcId = Number(userAuth.dcID);
  const isTest = userAuth.test;
  const keys: Record<number, string> = {};
  
  DC_IDS.forEach((dcId) => {
    try {
      const key = localStorage.getItem(`dc${dcId}_auth_key`);
      if (key) {
        keys[dcId] = JSON.parse(key);
      }
    } catch (error) {
    }
  });
  
  if (!Object.keys(keys).length) {
    return undefined;
  }
  
  const sessionData = {
    mainDcId,
    keys,
    isTest,
  };
  
  return sessionData;
}

function checkSessionLocked() {
  return localStorage.getItem(IS_SCREEN_LOCKED_CACHE_KEY) === 'true';
}

export function setCurrentUserIdForManagers(userId?: string) {
  if (!userId) {
    const sessionUserAuth = localStorage.getItem(SESSION_USER_KEY);
    if (sessionUserAuth) {
      try {
        const userAuth = JSON.parse(sessionUserAuth);
        userId = userAuth.id;
      } catch (err) {
      }
    }
  }

  if (!userId) {
    return;
  }

  const taskManager = require('./taskManager');
  const triggerManager = require('./triggerManager');
  const domainManager = require('./domainManager');
  
  if (taskManager && typeof taskManager.setCurrentUserId === 'function') {
    taskManager.setCurrentUserId(userId);
  }
  
  if (triggerManager && typeof triggerManager.setCurrentUserId === 'function') {
    triggerManager.setCurrentUserId(userId);
  }
  
  if (domainManager && typeof domainManager.setCurrentUserId === 'function') {
    domainManager.setCurrentUserId(userId);
  }
}

export function loadSessionString(): string | null {
  try {
    const sessionString = localStorage.getItem(SESSION_STRING_KEY);
    return sessionString;
  } catch (error) {
    return null;
  }
}

export function hasStoredSessionString(): boolean {
  return localStorage.getItem(SESSION_STRING_KEY) !== null;
}

export function getAllAccounts(): AccountData[] {
  try {
    const stored = localStorage.getItem(ACCOUNTS_KEY);
    if (!stored) return [];
    
    const accounts = JSON.parse(stored);
    return accounts;
  } catch (error) {
    return [];
  }
}

export function getCurrentAccount(): AccountData | null {
  try {
    const currentUserId = localStorage.getItem(CURRENT_ACCOUNT_ID_KEY);
    if (!currentUserId) return null;
    
    const accounts = getAllAccounts();
    const currentAccount = accounts.find(acc => acc.userId === currentUserId);
    
    return currentAccount || null;
  } catch (error) {
    return null;
  }
}

export function switchToAccount(userId: string): boolean {
  try {
    const accounts = getAllAccounts();
    const targetAccount = accounts.find(acc => acc.userId === userId);
    
    if (!targetAccount) {
      return false;
    }
    
    [
      SESSION_USER_KEY,
      'dc',
      CURRENT_ACCOUNT_ID_KEY,
      SESSION_STRING_KEY,
      ...DC_IDS.map((dcId) => `dc${dcId}_auth_key`),
      ...DC_IDS.map((dcId) => `dc${dcId}_hash`),
      ...DC_IDS.map((dcId) => `dc${dcId}_server_salt`),
    ].forEach((key) => {
      localStorage.removeItem(key);
    });
    
    try {
      const stringSession = new StringSession(targetAccount.sessionString);
      stringSession.load();
      const sessionData = stringSession.getSessionData();
      
      localStorage.setItem(SESSION_STRING_KEY, targetAccount.sessionString);
      localStorage.setItem(CURRENT_ACCOUNT_ID_KEY, userId);
      localStorage.setItem(SESSION_USER_KEY, JSON.stringify({
        dcID: sessionData.mainDcId,
        id: userId,
        test: sessionData.isTest,
      }));
      localStorage.setItem('dc', String(sessionData.mainDcId));
      
      if (typeof window !== 'undefined') {
        window.location.reload();
      }
      
      return true;
    } catch (error) {
      return false;
    }
  } catch (error) {
    return false;
  }
}

export function removeAccount(userId: string): boolean {
  try {
    const accounts = getAllAccounts();
    const filteredAccounts = accounts.filter(acc => acc.userId !== userId);
    
    if (filteredAccounts.length === accounts.length) {
      return false;
    }
    
    localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(filteredAccounts));
    
    const currentUserId = localStorage.getItem(CURRENT_ACCOUNT_ID_KEY);
    if (currentUserId === userId) {
      localStorage.removeItem(CURRENT_ACCOUNT_ID_KEY);
    }
    
    return true;
  } catch (error) {
    return false;
  }
}

export function clearAllAccounts() {
  localStorage.removeItem(ACCOUNTS_KEY);
  localStorage.removeItem(CURRENT_ACCOUNT_ID_KEY);
}

export function hasAccounts(): boolean {
  const accounts = getAllAccounts();
  return accounts.length > 0;
}